import React from "react";
import { useNavigate } from "react-router-dom";
import { useRenewal } from "./RenewalContext";
import RenewalSteps from "./RenewalSteps";
import ContactStep from "./steps/ContactStep";
import NotesStep from "./steps/NotesStep";
import PaymentStep from "./steps/PaymentStep";
import PmContactCard from "./steps/PmContactCard";
import { NOTICE_30_DAY, IMPORTANT_LEAD } from "./renewalCopy";
import { Card, Notice, ReviewRow, StepHeading, StepNav } from "./renewalUi";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { useStepParam } from "./useStepParam";
import { useRenewalSubmit } from "./useRenewalSubmit";
import RenewNowCard from "./steps/RenewNowCard";

// The "Term" step is intentionally omitted for now (see the updates doc);
// the renewal term itself is confirmed by the PM.
const STEPS = ["Contact", "Notes", "Payment", "Review"];

/**
 * "Renew My Lease" — Contact → Notes → Payment → Review → Confirmation.
 * Nothing is persisted yet; submitting just advances to the thank-you screen.
 */
const RenewFlow: React.FC = () => {
  const navigate = useNavigate();
  const { termUuid, draft, nextTerm } = useRenewal();
  const { methods } = usePaymentMethods();
  const { step, done: submitted, goTo, finish } = useStepParam(STEPS.length);
  const { submit, submitting, error: submitError } = useRenewalSubmit(finish);

  const back = () =>
    step === 1 ? navigate(`/renewal/${termUuid}`) : goTo(step - 1);
  const next = () => goTo(step + 1);

  const selectedMethod = methods.find((m) => m.id === draft.paymentMethodId);
  const methodLabel = selectedMethod
    ? `${selectedMethod.brand ?? selectedMethod.type} ••••${selectedMethod.last4 ?? ""}`
    : "—";

  if (submitted) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
          <svg
            className="h-7 w-7 text-success"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-primary">Thank you!</h2>
        <p className="mt-2 text-sm font-medium text-primary">
          You're all set to renew with Lighthouse.
        </p>
        <p className="mx-auto mt-1 max-w-md text-sm leading-relaxed text-gray-600">
          {/* Don't tell them we're chasing a confirmation that already
              exists — a confirmed term is what unlocks Renew Now below. */}
          {nextTerm?.status === "confirmed"
            ? "We've let your property manager know. Your renewal terms are already confirmed, so nothing else is needed from them."
            : "We've notified your property manager and requested they confirm your renewal terms in Lighthouse as soon as possible."}
        </p>

        {/* Only when the term is already confirmed — the card no-ops
            otherwise, and the other two flows never render it. */}
        <RenewNowCard />

        <PmContactCard />

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-6 cursor-pointer rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div>
      <RenewalSteps
        steps={STEPS}
        current={step}
        badge="renewal"
        // The renewal term's start date — when the new term takes effect.
        renewsOn={nextTerm?.startsOn}
      />

      {step === 1 && <ContactStep onBack={back} onNext={next} />}
      {step === 2 && <NotesStep onBack={back} onNext={next} />}
      {step === 3 && <PaymentStep onBack={back} onNext={next} />}

      {step === 4 && (
        <>
          <StepHeading
            title="Review & Submit"
            subtitle="Double-check everything before it goes to your PM."
          />

          <Card className="p-0">
            <ReviewRow label="Intent" value="Renew Lease" />
            <ReviewRow label="Name" value={draft.contact.name} />
            <ReviewRow label="Email" value={draft.contact.email} />
            <ReviewRow label="Phone" value={draft.contact.phone} />
            <ReviewRow label="Payment Method" value={methodLabel} />
            <ReviewRow
              label="Notes"
              value={
                draft.notes.hasNotes && draft.notes.text
                  ? draft.notes.text
                  : "None"
              }
            />
            <div className="px-5 py-3">
              <button
                type="button"
                onClick={() => goTo(1)}
                className="cursor-pointer text-sm font-semibold text-primary underline"
              >
                ← Make Changes
              </button>
            </div>
          </Card>

          <div className="mt-4">
            <Notice tone="warning">
              <strong>{IMPORTANT_LEAD}</strong> {NOTICE_30_DAY}
            </Notice>
          </div>

          {submitError && (
            <p className="mb-3 text-sm font-medium text-danger">{submitError}</p>
          )}

          <StepNav
            onBack={back}
            onNext={() => submit("renew")}
            nextLabel={
              submitting ? "Submitting…" : "Submit to Property Manager ✓"
            }
            nextDisabled={submitting}
            nextVariant="success"
          />
        </>
      )}
    </div>
  );
};

export default RenewFlow;
